import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import Button from '../../../components/common/Button';
import Modal from '../../../components/common/Modal';
import { 
  getSupplierSLAs, 
  getSupplierSLAById,
  downloadSLADocument,
  getSLAAttachments,
  downloadSLAAttachment
} from '../../../models/slaModel';

const SupplierSLAManagement = () => {
  const { supplier } = useSupplierAuth();
  const [slas, setSlas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSLA, setSelectedSLA] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    loadSLAs();
  }, []);

  const loadSLAs = async () => {
    setLoading(true);
    try {
      const response = await getSupplierSLAs();
      console.log('Full API response:', response); // Debug log
      
      if (response.status === 1) {
        // Extract array from nested structure: response.data.data should be the array
        const slasData = response.data?.data || [];
        console.log('Extracted SLAs data:', slasData); // Debug log
        console.log('Is array?', Array.isArray(slasData)); // Debug log
        
        setSlas(Array.isArray(slasData) ? slasData : []);
      } else {
        toast.error(response.message || 'Failed to load SLAs');
        setSlas([]);
      }
    } catch (error) {
      console.error('Error loading SLAs:', error);
      toast.error('Failed to load SLAs');
      setSlas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (sla) => {
    try {
      const response = await getSupplierSLAById(sla.id);
      if (response.status === 1) {
        setSelectedSLA(response.data);
        
        // Load attachments for this SLA
        try {
          const attachmentsResponse = await getSLAAttachments(sla.id);
          if (attachmentsResponse.status === 1) {
            setAttachments(attachmentsResponse.attachments || []);
          }
        } catch (attachmentError) { 
          setAttachments([]);
        }
        
        setShowDetailsModal(true);
      } else {
        toast.error(response.message || 'Failed to load SLA details');
      }
    } catch (error) {
      console.error('Error loading SLA details:', error);
      toast.error('Failed to load SLA details');
    }
  };

  const handleDownload = async (slaId) => {
    try {
      // Use the standardized attachment download
      const result = await downloadSLAAttachment(slaId);
      if (result.status === 1) {
        toast.success('Document download started');
      } else {
        toast.error(result.message || 'Download failed');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-800 bg-green-100';
      case 'expired': return 'text-red-800 bg-red-100';
      case 'expiring_soon': return 'text-yellow-800 bg-yellow-100';
      case 'draft': return 'text-gray-800 bg-gray-100';
      default: return 'text-gray-800 bg-gray-100';
    }
  };

  const getDaysUntilExpiry = (endDate) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = (endDate, notificationDays = 30) => {
    const daysUntilExpiry = getDaysUntilExpiry(endDate);
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= notificationDays) return 'expiring_soon';
    return 'active';
  };

  const filteredSLAs = slas.filter(sla => {
    const matchesSearch = sla.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const actualStatus = getExpiryStatus(sla.end_date, sla.notification_days);
    const matchesStatus = filterStatus === 'all' || actualStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Service Level Agreements</h1>
              <p className="text-gray-600">View and manage your SLAs</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by SLA title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="expiring_soon">Expiring Soon</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* SLA Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full flex justify-center items-center py-12">
                <div className="text-gray-500">Loading SLAs...</div>
              </div>
            ) : filteredSLAs.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No SLAs found
              </div>
            ) : (
              filteredSLAs.map((sla) => {
                const daysUntilExpiry = getDaysUntilExpiry(sla.end_date);
                const expiryStatus = getExpiryStatus(sla.end_date, sla.notification_days);
                
                return (
                  <div key={sla.sla_id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {sla.title}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(expiryStatus)}`}>
                          {expiryStatus.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        <div>
                          <span className="font-medium">Start Date:</span> {sla.start_date}
                        </div>
                        <div>
                          <span className="font-medium">End Date:</span> {sla.end_date}
                        </div>
                        <div>
                          <span className="font-medium">Days Until Expiry:</span> 
                          <span className={daysUntilExpiry <= sla.notification_days ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                            {daysUntilExpiry < 0 ? 'Expired' : `${daysUntilExpiry} days`}
                          </span>
                        </div>
                      </div>

                      {sla.description && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 line-clamp-3">{sla.description}</p>
                        </div>
                      )}

                      {/* Expiry Warning */}
                      {expiryStatus === 'expiring_soon' && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-yellow-800">
                              This SLA expires in {daysUntilExpiry} days
                            </span>
                          </div>
                        </div>
                      )}

                      {expiryStatus === 'expired' && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-red-800">
                              This SLA has expired
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleViewDetails(sla)}
                        >
                          View Details
                        </Button>
                        {sla.has_attachments && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(sla.id)}
                          >
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Details Modal */}
        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedSLA(null);
          }}
          title="SLA Details"
          maxWidth="max-w-2xl"
        >
          {selectedSLA && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {selectedSLA.title}
                </h3>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getExpiryStatus(selectedSLA.end_date, selectedSLA.notification_days))}`}>
                    {getExpiryStatus(selectedSLA.end_date, selectedSLA.notification_days).replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {selectedSLA.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-600">{selectedSLA.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Start Date</h4>
                  <p className="text-sm text-gray-600">{selectedSLA.start_date}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">End Date</h4>
                  <p className="text-sm text-gray-600">{selectedSLA.end_date}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Notification Period</h4>
                <p className="text-sm text-gray-600">
                  {selectedSLA.notification_days} days before expiry
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Days Until Expiry</h4>
                <p className={`text-sm font-medium ${getDaysUntilExpiry(selectedSLA.end_date) <= selectedSLA.notification_days ? 'text-red-600' : 'text-green-600'}`}>
                  {getDaysUntilExpiry(selectedSLA.end_date) < 0 ? 'Expired' : `${getDaysUntilExpiry(selectedSLA.end_date)} days`}
                </p>
              </div>

              {selectedSLA.created_at && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Created</h4>
                  <p className="text-sm text-gray-600">{new Date(selectedSLA.created_at).toLocaleDateString()}</p>
                </div>
              )}

              {selectedSLA.has_attachments && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => handleDownload(selectedSLA.sla_id)}
                    className="w-full"
                  >
                    Download SLA Document
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SupplierSLAManagement;